import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react';
import * as api from '../api.ts';
import { useCountdown } from '../countdown.ts';
import { BuildingIcon, ExitIcon, LockIcon, MegaphoneIcon } from '../icons.tsx';
import { Map, MapKey, type MapPoint } from '../map.tsx';
import { Ring } from '../ring.tsx';
import { StreamTile } from '../stream-tile.tsx';
import {
  Button,
  Cell,
  Empty,
  Field,
  Finding,
  InlineAction,
  Label,
  LoadError,
  Metric,
  Metrics,
  Mono,
  PageTitle,
  Row,
  Section,
  Segmented,
  Select,
  Spinner,
  Status,
  Table,
  sentence,
  type Tone,
} from '../ui.tsx';
import { humanRole } from './readiness.tsx';
import { useDisclosure } from '../lib/disclosure.ts';
import { WardenAreas, getWardenAreas } from '../warden-areas.tsx';
import {
  GroundPane,
  LENS_WORDS,
  groundPointsOf,
  ReportsPane,
  newReportCount,
  orderPanes,
  paneCount,
  reportFinding,
  useDirect,
  type LiveLens,
} from '../direct.tsx';
import { ReverifyModal } from '../reverify.tsx';
import { PROCEDURES, resolveStoredInstruction } from '../lib/procedures.ts';

/**
 * The event-type glyph for a row — a leading affordance on the Activity feed,
 * paired with the procedure word (never instead of it). currentColor, so it
 * themes with the row; aria-hidden, so the word is the accessible content.
 */
function ProcedureGlyph({ procedure }: { procedure: string }) {
  const glyph =
    procedure === 'lockdown' ? (
      <LockIcon size={16} />
    ) : procedure === 'invacuation' ? (
      <BuildingIcon size={16} />
    ) : procedure === 'communication' ? (
      <MegaphoneIcon size={16} />
    ) : (
      <ExitIcon size={16} />
    );
  return (
    <span aria-hidden className="shrink-0 text-[var(--ink-2)]">
      {glyph}
    </span>
  );
}

/**
 * The words for each of the server's person statuses, and the order they are
 * listed in — the people who need something first.
 */
const STATUS_ORDER: api.PersonStatus[] = [
  'needs_help',
  'not_reached',
  'no_answer',
  'arrived',
  'not_on_site',
  'safe_arrived',
  'safe',
];
/** Statuses that are an answer from the person (or their stated absence). */
const RESPONDED = new Set<api.PersonStatus>(['needs_help', 'safe', 'safe_arrived', 'not_on_site']);
const STATUS_WORDS: Record<api.PersonStatus, { chip: string; row: string; tone: Tone }> = {
  needs_help: { chip: 'Need help', row: 'Needs help', tone: 'alert' },
  not_reached: { chip: 'Not reached', row: 'Not reached', tone: 'attention' },
  no_answer: { chip: 'No answer', row: 'No answer yet', tone: 'attention' },
  arrived: { chip: 'At a point, no answer', row: 'At a point, no answer', tone: 'attention' },
  not_on_site: { chip: 'Not on site', row: 'Safe — not on site', tone: 'neutral' },
  safe_arrived: { chip: 'Safe at a point', row: 'Safe', tone: 'safe' },
  safe: { chip: 'Safe', row: 'Safe — told us', tone: 'safe' },
};

/* ---------------------------------------------------------------- activate */

/**
 * The raise panel lives in `raise.tsx` — a glass panel over the persistent map
 * that raises on one or several buildings, shows the points people are guided
 * to, and previews the message. Re-exported here because the shell and the
 * tests import it from the events screen.
 */
export { Activate } from './raise.tsx';

/**
 * Change the procedure of a live event (migration 0076,
 * `docs/receiving-experience-map.md` §Transitions).
 *
 * A modal, because a procedure change is a new alert to everyone and must be a
 * deliberate act, not a stray tap on the command bar. It states what will
 * happen before it sends — the same care the raise flow takes — and it will
 * not let the operator send the one combination the doc forbids: a lockdown
 * that is not silent. The server enforces that too; the disabled action and the
 * line beneath it are the earlier, kinder version.
 */
export function TransitionDialog({
  currentProcedure,
  stored,
  pending,
  error,
  onSubmit,
  onDismiss,
}: {
  currentProcedure: string;
  /**
   * The building's stored procedure wording (migration 0093). Prefer these
   * words over the built-in default when resolving the instruction for the
   * procedure being transitioned to — the same resolution the raise flow uses
   * (`resolveStoredInstruction`), so escalating mid-incident does not silently
   * revert a building's own words to the generic line.
   */
  stored: readonly api.SiteProcedure[] | undefined;
  pending: boolean;
  error: unknown;
  onSubmit: (body: api.TransitionBody) => void;
  onDismiss: () => void;
}) {
  // Every procedure but the one already in force — a transition goes somewhere.
  const options = PROCEDURES.filter((p) => p.value !== currentProcedure);
  const [next, setNext] = useState<(typeof PROCEDURES)[number]['value']>(options[0]!.value);
  const [instruction, setInstruction] = useState(() =>
    resolveStoredInstruction(options[0]!.value, stored),
  );
  const [edited, setEdited] = useState(false);

  const pick = (v: (typeof PROCEDURES)[number]['value']) => {
    setNext(v);
    // Never overwrite words the operator typed — during an incident that
    // sentence may be the only thing anyone knows about what is happening.
    // When they have not typed, prefer this building's own stored words for the
    // new procedure, falling back to the built-in default.
    if (!edited) setInstruction(resolveStoredInstruction(v, stored));
  };

  const silent = next === 'lockdown';
  const message =
    api.errorMessage(error) ??
    (instruction.trim() === '' ? 'Write the new instruction before sending.' : null);
  const canSend = instruction.trim() !== '' && !pending;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Change the procedure"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
    >
      <div className="theme-dark w-full max-w-[560px] rounded-[var(--radius-2)] border border-[var(--line)] bg-[var(--paper)] p-6 text-[var(--ink)]">
        <h2 className="text-[19px] font-[650]">Change the procedure</h2>
        <p className="mt-1 text-[14px] text-[var(--ink-2)]">
          Currently a {sentence(currentProcedure).toLowerCase()}. This sends a new alert to everyone
          already told, under the new procedure — their earlier answer stops counting and they are
          asked again.
        </p>

        <div className="mt-5 space-y-4">
          <Select
            label="New procedure"
            value={next}
            onChange={(v) => pick(v as (typeof PROCEDURES)[number]['value'])}
            options={options.map((p) => ({ value: p.value, label: p.label }))}
          />
          {silent ? (
            <p className="text-[13px] leading-[18px] text-[var(--ink-2)]">
              A lockdown transition is delivered silently — movement is the danger and a chime can
              be the thing that gives somebody away, so the phones it reaches will not make one.
            </p>
          ) : null}
          <Field
            label="What they will be told"
            value={instruction}
            onChange={(v) => {
              setEdited(true);
              setInstruction(v);
            }}
          />
        </div>

        <p className="mt-5 text-[15px]">
          This becomes a <strong>{sentence(next).toLowerCase()}</strong>
          {silent ? ', delivered silently' : ''}. Everyone alerted is told at once and asked to
          answer again.
        </p>

        {message ? (
          <div className="mt-4">
            <Finding tone="alert">{message}</Finding>
          </div>
        ) : null}

        <div className="mt-6 flex flex-wrap gap-3">
          <Button
            variant="primary"
            danger
            disabled={!canSend}
            onClick={() => onSubmit({ procedure: next, instruction })}
          >
            {pending ? 'Sending…' : `Send the ${sentence(next).toLowerCase()}`}
          </Button>
          <Button variant="quiet" onClick={onDismiss} disabled={pending}>
            Keep the {sentence(currentProcedure).toLowerCase()}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------- live */

/**
 * What is happening, in one sentence.
 *
 * Every clause has to be true of the numbers beside it, because this is the
 * screen the recurring state-asserting bug keeps landing on: a heading that
 * claims an outcome the data does not support is worse here than no heading at
 * all. So "everyone" is only said when nobody is outstanding, and a site with
 * nobody notified says exactly that rather than dividing by zero into a
 * reassuring 100%.
 *
 * People who asked for help lead, because they are the only number on this
 * screen that names somebody waiting on a person rather than a count.
 */
export function situation(a: {
  notified: number;
  safe: number;
  needsHelp: number;
  unaccounted: number;
  /**
   * The arrival moment (migration 0075). Optional so every existing caller and
   * every prior assertion keeps its exact wording — the caveat is appended only
   * when arrival-marking is in use (`arrived > 0`) and somebody who answered
   * safe has not reached the assembly point. Safe is not clear: a person can
   * report safe from inside the building.
   */
  arrived?: number;
  safeNotArrived?: number;
}) {
  if (a.notified === 0) return 'Nobody has been notified yet.';

  const help =
    a.needsHelp > 0
      ? `${a.needsHelp} ${a.needsHelp === 1 ? 'person has' : 'people have'} asked for help. `
      : '';

  const base =
    a.unaccounted === 0
      ? `${help}Everyone asked has answered safe.`
      : `${help}${a.safe} of ${a.notified} have answered safe, ` +
        `${a.unaccounted} ${a.unaccounted === 1 ? 'has' : 'have'} not.`;

  // Safe is not clear. Only when arrival was actually being recorded and a gap
  // exists — otherwise a site that never marks arrival would read as having a
  // disagreement it never measured.
  const gap = a.safeNotArrived ?? 0;
  if ((a.arrived ?? 0) > 0 && gap > 0) {
    return (
      `${base} ${gap} ${gap === 1 ? 'has' : 'have'} answered safe but ` +
      `${gap === 1 ? 'is' : 'are'} not yet at the assembly point — safe is not the same as clear.`
    );
  }
  return base;
}

/** The sentence for a live request that ended without anything being sent. */
export function unsentSentence(state: string, a: api.EventSummary['authorisation']): string | null {
  const mine = a?.raisedBy.you ?? false;
  if (state === 'cancelled') {
    if (a?.stoppedBy) {
      if (a.stoppedBy.you)
        return a.stoppedBy.withdrawn || mine
          ? 'You withdrew this request. Nothing was sent.'
          : 'You rejected this. Nothing was sent.';
      if (a.stoppedBy.withdrawn)
        return `${a.stoppedBy.label} withdrew their request. Nothing was sent.`;
      return `Rejected by ${a.stoppedBy.label} — nothing was sent.`;
    }
    return 'This request was stopped. Nothing was sent.';
  }
  if (state === 'expired' || (state === 'pending_second_confirm' && a?.status === 'lapsed')) {
    return mine
      ? 'Nobody authorised your request in time, so it lapsed. Nothing was sent.'
      : 'This request lapsed. Nothing was sent.';
  }
  return null;
}

/**
 * The detail view for an event that has been raised but **not sent** — it is
 * sitting in `pending_second_confirm`, waiting for a second admin to authorise
 * it (R1, `docs/security.md` §1.1).
 *
 * This is deliberately NOT the live command view. Nothing has gone out, there
 * are no recipients, and no accountability answer exists yet — so it shows no
 * ring, no notified/safe/no-answer counts, no "N people told" and no
 * command controls. Rendering any of those would assert an alert that was never
 * sent: the "screen asserts a state that is not true" bug this repo has hit
 * repeatedly (a pending event that *looks* active can make an operator believe
 * an evacuation launched when it did not). What it shows instead is the plain
 * truth — this is waiting, nobody has been told, here is what *would* be sent —
 * and the two ways to resolve it.
 *
 * The authorise action is offered, but the server refuses it for the person who
 * raised it (`second_person_required`, a 403): R1's second pair of eyes must be
 * a *different* admin. The raiser sees that refusal rendered as "waiting on
 * your other admins" rather than a raw error string.
 */
function AwaitingAuthorisation({
  orgId,
  event: e,
  authorisation,
  destination,
  onBack,
}: {
  orgId: string;
  event: api.EventSummary['event'];
  authorisation: api.EventSummary['authorisation'];
  destination: NonNullable<api.EventSummary['destination']> | null;
  onBack: () => void;
}) {
  const qc = useQueryClient();
  const eventId = e.id;

  const authorise = useMutation({
    mutationFn: () => api.authoriseEvent(orgId, eventId),
    // On success the event moves to `active`; the parent Live query is
    // invalidated so it refetches and re-renders as the live command view.
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['event', orgId, eventId] }),
  });
  const cancel = useMutation({
    mutationFn: () => api.cancelEvent(orgId, eventId),
    onSuccess: onBack,
  });

  // R1's second pair of eyes cannot be the raiser — the server 403s
  // `second_person_required`. Render that as the honest "waiting on your other
  // admins" rather than a raw refusal, and disable the button so the raiser
  // does not keep hitting a door that will never open for them.
  const raiserRefused =
    authorise.error instanceof api.ApiError &&
    (authorise.error.detail.reason === 'second_person_required' ||
      /second[ _](person|admin)/i.test(authorise.error.message));

  const drill = e.mode === 'drill';

  return (
    <div className="min-h-screen bg-[var(--paper)] text-[var(--ink)]">
      <div className="mx-auto w-full max-w-[720px] px-6 py-10">
        <div className="mb-8 flex items-start justify-between gap-6">
          <div>
            <div className="mb-2">
              <Status tone="attention">Not sent — awaiting authorisation</Status>
            </div>
            <h1 className="text-[25px] leading-[31px] font-[650] tracking-[-0.02em]">
              {e.cap_instruction}
            </h1>
            <p className="mt-1 text-[14px] text-[var(--ink-2)]">
              {drill ? 'Drill' : 'Live emergency'} · {e.cap_area_desc}
              {authorisation
                ? ` · asked by ${authorisation.raisedBy.you ? 'you' : authorisation.raisedBy.label}`
                : null}
            </p>
          </div>
          <Button variant="quiet" onClick={onBack} label="Leave this event">
            Back
          </Button>
        </div>

        <Finding tone="attention">
          This has not been sent. Nobody has been alerted — it is waiting for a second admin to
          authorise it before anything goes out, and the authorisation lapses on its own if nobody
          does, because two people eventually is one person with extra steps.
        </Finding>

        {/*
          Where people would be sent, and what the raiser closed — the second
          pair of eyes must see this before authorising, not discover it on
          the live view after four hundred people have set out.
        */}
        {destination && (destination.point || destination.closedPoints.length > 0) ? (
          <p className="mt-4 text-[14px] leading-[20px]">
            {destination.point ? (
              <>
                People would be sent to <strong>{destination.point.name}</strong>.
              </>
            ) : null}
            {destination.closedPoints.length > 0 ? (
              <>
                {' '}
                Closed before sending:{' '}
                {destination.closedPoints.map((c, i) => (
                  <span key={c.id}>
                    {i > 0 ? ', ' : ''}
                    <strong>{c.name}</strong>
                    {c.redirectTo ? ` (go to ${c.redirectTo})` : ''}
                  </span>
                ))}
                .
              </>
            ) : null}
          </p>
        ) : null}

        {raiserRefused ? (
          <div className="mt-4">
            <Finding tone="attention">
              You raised this, so it needs a different admin to authorise it — R1&rsquo;s second
              pair of eyes has to be someone other than the person who raised it. Waiting on your
              other admins.
            </Finding>
          </div>
        ) : authorise.error ? (
          <div className="mt-4">
            <Finding>{api.errorMessage(authorise.error)}</Finding>
          </div>
        ) : null}

        <div className="mt-8 flex flex-wrap gap-3">
          <Button
            variant="primary"
            size="live"
            disabled={authorise.isPending || raiserRefused}
            onClick={() => authorise.mutate()}
          >
            {authorise.isPending ? 'Authorising…' : 'Authorise and send'}
          </Button>
          <Button
            variant="quiet"
            size="live"
            disabled={cancel.isPending}
            onClick={() => cancel.mutate()}
            label="Withdraw this event without sending it"
          >
            {cancel.isPending ? 'Withdrawing…' : 'Withdraw it'}
          </Button>
        </div>

        {cancel.error ? (
          <div className="mt-4">
            <Finding>{api.errorMessage(cancel.error)}</Finding>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * The live command view. **Dark regardless of preference**, full-bleed, and it
 * shows four things: the ring, the count, who has not answered, and stand down.
 *
 * The dark field is not a style choice. It cuts glare in a stairwell or a
 * darkened room, and it leaves the red band and the ring as the only bright
 * things — which is exactly this view's hierarchy. A mode change is also a
 * signal in itself: the screen looking different is part of knowing something
 * is happening.
 *
 * Red is a band and a dot. Never a field, never pulsing.
 */
export function Live({
  orgId,
  eventId,
  onBack,
}: {
  orgId: string;
  eventId: string;
  onBack: () => void;
}) {
  const qc = useQueryClient();

  const event = useQuery({
    queryKey: ['event', orgId, eventId],
    queryFn: () => api.getEvent(orgId, eventId),
    // An operator refreshing during an incident wants the truth, not a count
    // from thirty seconds ago.
    refetchInterval: 5_000,
  });

  const sweeps = useQuery({
    queryKey: ['sweeps', orgId, eventId],
    queryFn: () => api.getSweeps(orgId, eventId),
    refetchInterval: 10_000,
  });

  const command = useQuery({
    queryKey: ['command', orgId, eventId],
    queryFn: () => api.getCommand(orgId, eventId),
  });

  // Progressive-disclosure gates for enterprise-oriented surfaces on the live
  // view (`docs/progressive-disclosure.md`). Phase 1 answers `true` for every
  // surface, so nothing changes on screen; the wiring lives here so Phase 2
  // can hide the sweep board on a one-zone café without touching this file.
  const disclosure = useDisclosure(orgId);

  const siteId = event.data?.event.site_id ?? '';

  const sites = useQuery({ queryKey: ['sites', orgId], queryFn: () => api.getSites(orgId) });
  const assembly = useQuery({
    queryKey: ['assembly', orgId, siteId],
    queryFn: () => api.getAssemblyPoints(orgId, siteId),
    enabled: Boolean(siteId),
  });

  /**
   * The building's own stored procedure wording (migration 0093). The SAME
   * query key the raise flow uses, so it is cache-shared: an admin who raised
   * from this building already has it warm. It feeds the transition dialog, so
   * escalating evacuation→lockdown mid-incident lands on the building's own
   * lockdown words rather than the generic built-in line.
   */
  const storedProcedures = useQuery({
    queryKey: ['siteProcedures', orgId, siteId],
    queryFn: () => api.siteProcedures(orgId, siteId),
    enabled: Boolean(siteId),
  });

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['event', orgId, eventId] });

  const me = useQuery({ queryKey: ['me'], queryFn: api.getMe });

  /**
   * The Direct record — the ground, the update log, the inbound reports — and
   * the role lens. The warden board (cache-shared with the Areas pane, same
   * key) decides the default lens: somebody the server gives only their own
   * areas is a warden and sees those first; everybody else opens on command.
   * The chips in the header override it for this visit.
   */
  const direct = useDirect(orgId, eventId);
  const board = useQuery({
    queryKey: ['wardenAreas', orgId, eventId],
    queryFn: () => getWardenAreas(orgId, eventId),
    refetchInterval: 5_000,
  });
  const [lensChoice, setLensChoice] = useState<LiveLens | null>(null);
  const lens: LiveLens = lensChoice ?? (board.data?.scope === 'mine' ? 'warden' : 'command');

  /**
   * Held retry for a `stale_session` refusal (docs/security.md R1). When
   * the freshness gate refuses one of the four transitions below we open
   * the ReverifyModal, then call the retry it captures. The operator never
   * has to sign out mid-incident.
   */
  const [reverify, setReverify] = useState<null | (() => void)>(null);
  const onFreshError =
    (retry: () => void) =>
    (e: Error): void => {
      if (e instanceof api.ApiError && e.detail.code === 'stale_session') {
        setReverify(() => retry);
      }
    };

  const standDown = useMutation({
    mutationFn: () => api.allClear(orgId, eventId),
    onSuccess: invalidate,
    onError: onFreshError(() => standDown.mutate()),
  });
  const close = useMutation({
    mutationFn: () => api.closeEvent(orgId, eventId),
    onSuccess: invalidate,
    onError: onFreshError(() => close.mutate()),
  });
  /**
   * The ordinary post-window correction — the "Sent in error" control that
   * has always been here, kept for events that never took the preview-and-
   * retract path and for events whose window has passed. Semantically the
   * same transition as `/retract`; the difference is what gets recorded on
   * `retracted_within_window` and read by signed reports.
   */
  const correct = useMutation({
    mutationFn: () => api.correctEvent(orgId, eventId),
    onSuccess: invalidate,
    onError: onFreshError(() => correct.mutate()),
  });
  /**
   * The R3-as-primary-control retract, only offered while
   * `retract_deadline_at` is in the future (migration 0064). After the
   * deadline the button disappears and the ordinary Correct button remains
   * — the transition is the same, but the label must not claim a safety
   * window that has closed.
   */
  const retract = useMutation({
    mutationFn: () => api.retractEvent(orgId, eventId),
    onSuccess: invalidate,
    onError: onFreshError(() => retract.mutate()),
  });

  /**
   * Change the procedure mid-incident — the transition (migration 0076,
   * `docs/receiving-experience-map.md` §Transitions). Opens a flow that states
   * what will happen before it sends anything, the same care the raise flow
   * takes: a procedure change is a new alert to everyone, and swapping it
   * silently would look like a UI bug to a person walking to an assembly point.
   */
  const [changing, setChanging] = useState(false);
  const changeProcedure = useMutation({
    mutationFn: (body: api.TransitionBody) => api.transitionEvent(orgId, eventId, body),
    onSuccess: () => {
      setChanging(false);
      invalidate();
    },
  });

  /**
   * Seconds left in the R3-as-primary-control safety window (migration 0064).
   *
   * `null` when the event never took the preview-and-retract path, when the
   * deadline is already in the past, when the event has moved out of
   * `active`, or when the event has not loaded yet. The button that renders
   * it disappears in every one of those cases — the ordinary Correct button
   * remains, so retract is never gone, just no longer wearing the "one-tap
   * safety" affordance it does not have any more.
   *
   * Called *above* the loading early-return: React's hook order must stay
   * stable across renders, and gating it behind `event.data` would land the
   * next render one hook ahead.
   */
  const retractSecondsLeft = useCountdown(
    event.data?.event.state === 'active' ? (event.data.event.retract_deadline_at ?? null) : null,
  );

  /** The People list's status filter (owner, 2026-10-13). */
  const [peopleFilter, setPeopleFilter] = useState<'all' | api.PersonStatus>('all');

  // A one-second clock for "since alert" and the ages in the help queue.
  // Above the loading guard like every hook here.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // What goes on the map: the building, every placed assembly point, and every
  // zone with coordinates coloured by sweep state. Memoised and kept up here
  // WITH THE HOOKS (above the loading guard, so it is never called
  // conditionally): the live view re-renders on every poll — counts,
  // countdowns — and a fresh `points` array each render would re-run the map
  // engines' marker effect (and, before the create-once fix, tear the map down)
  // on every tick. Self-contained so it recomputes only when its data changes.
  const points: MapPoint[] = useMemo(() => {
    const ev = event.data?.event;
    if (!ev) return [];
    const pts: MapPoint[] = [];
    const s = sites.data?.sites.find((x) => x.id === ev.site_id);
    if (s?.latitude != null && s.longitude != null) {
      pts.push({
        id: s.id,
        latitude: s.latitude,
        longitude: s.longitude,
        label: s.name,
        kind: 'site',
      });
    }
    // Only the points this procedure sends people to — the raise panel's
    // rule: evacuation → outdoor points, lockdown → points inside, invacuation
    // and communication name none. A reunification point is not where anybody
    // is being sent during the event.
    const kindFor =
      ev.procedure === 'evacuation'
        ? 'outdoor_assembly'
        : ev.procedure === 'lockdown'
          ? 'indoor_shelter'
          : null;
    for (const p of assembly.data?.assemblyPoints ?? []) {
      if (p.kind !== kindFor) continue;
      pts.push({
        id: p.id,
        latitude: p.latitude,
        longitude: p.longitude,
        label: p.name,
        kind: 'assembly',
      });
    }
    for (const z of sweeps.data?.zones ?? []) {
      if (z.latitude == null || z.longitude == null) continue;
      pts.push({
        id: z.zoneId,
        latitude: z.latitude,
        longitude: z.longitude,
        label: z.zoneName,
        kind:
          z.state === 'clear'
            ? 'clear'
            : z.state === 'cannot_access'
              ? 'blocked'
              : z.state === 'not_started'
                ? 'unswept'
                : 'sweeping',
      });
    }
    return pts;
  }, [event.data, sites.data, assembly.data, sweeps.data]);

  // A failed load is an answer, not a longer wait. An errored query has no
  // data, so folding it into the spinner branch renders "Loading…" forever —
  // which an operator mid-incident cannot tell from an outage. (An expired
  // session routes to sign-in via `onSessionExpired` before this renders; this
  // is the network-down / server-error case.)
  if (event.isError) {
    return (
      <div className="theme-dark min-h-screen bg-[var(--paper)] p-8 text-[var(--ink)]">
        <LoadError onRetry={() => void event.refetch()}>
          {api.errorMessage(event.error) ?? 'This event could not be loaded.'}
        </LoadError>
      </div>
    );
  }

  if (event.isLoading || !event.data) {
    return (
      <div className="theme-dark min-h-screen bg-[var(--paper)] p-8 text-[var(--ink)]">
        <Spinner />
      </div>
    );
  }

  // The server answered with the RECIPIENT's trimmed view (2026-09-22): this
  // person was alerted but holds no operator read on the org, and the
  // accountability block is zeroed wire-compat filler. Rendering it would put
  // "0 notified" in front of somebody during a real event — the
  // state-asserting class — so the command view refuses it in words instead.
  if (event.data.receiverView) {
    return (
      <div className="theme-dark min-h-screen bg-[var(--paper)] p-8 text-[var(--ink)]">
        <p className="max-w-[480px] text-[15px]">
          This is the operator&rsquo;s command view, and your account receives alerts here rather
          than running them. Answer from the alert you were sent, or ask an administrator for
          access.
        </p>
      </div>
    );
  }

  const { event: e, accountability: a } = event.data;

  // A raised-but-unsent event is NOT the live command view. It has no
  // recipients and no accountability answer, so it renders the
  // awaiting-authorisation state instead of the ring/counts/map — otherwise a
  // pending event looks like a launched one (the state-assertion bug class).
  // A live request that ended without being sent — rejected, withdrawn or
  // lapsed — says so in words. Rendering it as the command view would draw a
  // ring over an alert nobody received (the state-asserting class).
  const unsent = e.mode === 'live' ? unsentSentence(e.state, event.data.authorisation) : null;
  if (unsent) {
    return (
      <div className="min-h-screen bg-[var(--paper)] text-[var(--ink)]">
        <div className="mx-auto w-full max-w-[720px] px-6 py-10">
          <div className="mb-8 flex items-start justify-between gap-6">
            <div>
              <div className="mb-2">
                <Status tone="attention">Not sent</Status>
              </div>
              <h1 className="text-[25px] leading-[31px] font-[650] tracking-[-0.02em]">{unsent}</h1>
              <p className="mt-1 text-[14px] text-[var(--ink-2)]">{e.cap_instruction}</p>
            </div>
            <Button variant="quiet" onClick={onBack} label="Leave this event">
              Back
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (e.state === 'pending_second_confirm') {
    return (
      <AwaitingAuthorisation
        orgId={orgId}
        event={e}
        authorisation={event.data.authorisation ?? null}
        destination={event.data.destination ?? null}
        onBack={onBack}
      />
    );
  }

  const live = e.state === 'active';
  const drill = e.mode === 'drill';

  /**
   * What goes on the map: the building, every assembly point, and every zone
   * somebody has placed, coloured by sweep state.
   *
   * Zones without coordinates are simply absent — migration 0029 made the point
   * optional on purpose, and inventing one would put an unswept area on the
   * wrong side of the building, which is worse than leaving it off. The board
   * below always carries every zone, so nothing is hidden by this.
   */
  // `points` for the map is computed with the hooks above (before the loading
  // guard, so the hook is never called conditionally).
  const unplacedZones = (sweeps.data?.zones ?? []).filter((z) => z.latitude == null).length;

  /**
   * What needs a human now, as a list of things to do — never a statistic.
   * Computed from the same data the panes render, so it can never disagree
   * with them. Empty is a sentence, not a blank.
   */
  const doNext: { key: string; tone: 'alert' | 'attention'; text: string }[] = [];
  if (
    disclosure.isVisible('twoModelContradiction') &&
    sweeps.data?.allAreasClear &&
    a.unaccounted > 0
  ) {
    doNext.push({
      key: 'contradiction',
      tone: 'alert',
      text: `Every area has been reported clear, but ${a.unaccounted} ${
        a.unaccounted === 1 ? 'person has' : 'people have'
      } not answered. Do not send anybody back in on the sweep alone.`,
    });
  }
  for (const p of event.data.needsHelpQueue) {
    const mins = Math.max(0, Math.round((now - new Date(p.created_at).getTime()) / 60000));
    doNext.push({
      key: `help-${p.user_id}`,
      tone: 'alert',
      text: `${p.label} needs help — ${mins}m ago${p.note ? `: “${p.note}”` : ''}`,
    });
  }
  for (const z of sweeps.data?.zones ?? []) {
    if (z.state === 'cannot_access')
      doNext.push({
        key: `blocked-${z.zoneId}`,
        tone: 'alert',
        text: `${z.zoneName} could not be reached.`,
      });
    else if (!z.warden && z.state !== 'clear')
      doNext.push({
        key: `nowarden-${z.zoneId}`,
        tone: 'attention',
        text: `${z.zoneName} has nobody assigned to sweep it.`,
      });
  }
  if (a.arrived > 0 && a.safeNotArrived > 0) {
    doNext.push({
      key: 'not-arrived',
      tone: 'attention',
      text: `${a.safeNotArrived} answered safe but ${a.safeNotArrived === 1 ? 'has' : 'have'} not been recorded at an assembly point — safe is not clear.`,
    });
  }
  if (event.data.unreachable.length > 0) {
    doNext.push({
      key: 'unreachable',
      tone: 'attention',
      text: `${event.data.unreachable.length} not reached by anything: ${event.data.unreachable
        .map((p) => p.label)
        .join(', ')}. Unaccounted because nobody asked them, not because they did not reply.`,
    });
  }
  // A reading several people agree on, or anybody reporting a person in
  // trouble — one person's unconfirmed hazard stays on the Reports pane.
  for (const r of direct.reports.data?.reports ?? []) {
    const text = reportFinding(r);
    if (text)
      doNext.push({
        key: `report-${r.id}`,
        tone: r.kind === 'needs_help' ? 'alert' : 'attention',
        text,
      });
  }
  // The map's rule for which points this procedure uses (below), applied to
  // the ground pane and its count too, so the two never disagree.
  const pointKind: api.AssemblyKind | null =
    e.procedure === 'evacuation'
      ? 'outdoor_assembly'
      : e.procedure === 'lockdown'
        ? 'indoor_shelter'
        : null;
  const closedPoints = groundPointsOf(direct.ground.data?.points, pointKind).filter(
    (pt) => !pt.open,
  ).length;
  const hazards = direct.ground.data?.zones.filter((z) => z.hazard).length ?? 0;
  const groundCount =
    [
      closedPoints > 0 ? `${closedPoints} ${closedPoints === 1 ? 'point' : 'points'} closed` : null,
      hazards > 0 ? `${hazards} ${hazards === 1 ? 'hazard' : 'hazards'}` : null,
    ]
      .filter(Boolean)
      .join(' · ') || undefined;

  /**
   * Everybody who was told, with the server's one status each, plus the PEEP
   * line for anybody who has one and is not yet safe. Filterable by status.
   */
  const assistanceByUser: Record<
    string,
    { plan: string; assistant: string | null; goesTo: string | null }
  > = Object.fromEntries(
    event.data.assistance
      .filter((x) => !x.confirmed_safe)
      .map((x) => [
        x.user_id,
        { plan: x.assistance_required, assistant: x.assistant, goesTo: x.assembly_point },
      ]),
  );
  const people = event.data.people ?? [];
  const statusCounts = people.reduce<Partial<Record<api.PersonStatus, number>>>((acc, p) => {
    acc[p.status] = (acc[p.status] ?? 0) + 1;
    return acc;
  }, {});
  const responded = people.filter((p) => RESPONDED.has(p.status)).length;
  const shown = (peopleFilter === 'all' ? people : people.filter((p) => p.status === peopleFilter))
    .slice()
    .sort((x, y) => STATUS_ORDER.indexOf(x.status) - STATUS_ORDER.indexOf(y.status));

  const elapsed = (() => {
    if (!e.activated_at) return '—';
    const s = Math.max(0, Math.floor((now - new Date(e.activated_at).getTime()) / 1000));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return h > 0
      ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
      : `${m}:${String(sec).padStart(2, '0')}`;
  })();
  const siteName = sites.data?.sites.find((x) => x.id === e.site_id)?.name ?? e.site_name;

  return (
    <div className="theme-dark flex h-screen flex-col overflow-hidden bg-[var(--paper)] text-[var(--ink)]">
      {reverify && me.data?.email ? (
        <ReverifyModal
          email={me.data.email}
          onClose={() => setReverify(null)}
          onVerified={() => {
            const retry = reverify;
            setReverify(null);
            retry();
          }}
        />
      ) : null}

      {/*
        The top strip: what this is, how long it has been running, and the
        controls. The band chip is the only red on the screen. Nothing here
        scrolls; the dashboard is the viewport (owner, 2026-10-13).
      */}
      <header className="flex shrink-0 items-center gap-4 border-b border-[var(--line)] px-5 py-2.5">
        <span
          className="shrink-0 rounded-[var(--radius-1)] px-2.5 py-1 text-[12px] font-medium tracking-[0.06em] text-white uppercase"
          style={{ background: drill ? 'var(--accent)' : 'var(--alert)' }}
        >
          {drill ? 'Drill' : 'Live emergency'}
        </span>
        <div className="min-w-0">
          <h1 className="truncate text-[17px] leading-[22px] font-[650] tracking-[-0.01em]">
            {e.procedure ? sentence(e.procedure) : e.cap_event} · {siteName}
          </h1>
          <p className="truncate text-[13px] leading-[18px] text-[var(--ink-2)]">
            {e.cap_instruction} · {e.cap_area_desc} · {sentence(e.state.replace(/_/g, ' '))}
            {e.audience_basis === 'presence'
              ? ' · counted from who was signed in as present'
              : e.audience_basis === 'roster'
                ? ' · counted from your people list, not from who was actually here'
                : ''}
            {event.data.followersTold > 0
              ? ` · ${event.data.followersTold} follower${event.data.followersTold === 1 ? '' : 's'} of this place told`
              : ''}
          </p>
        </div>
        <div className="ml-auto flex shrink-0 items-center gap-3">
          {/* The role lens: which panes come first on the right. Auto-picked
              from what the server gives this person; a chip overrides it. */}
          <div role="group" aria-label="View as" className="mr-2 flex gap-1">
            {(['command', 'warden', 'security'] as const).map((k) => (
              <button
                key={k}
                type="button"
                aria-pressed={lens === k}
                onClick={() => setLensChoice(k)}
                className={`rounded-[var(--radius-1)] border px-2 py-1 text-[12px] ${
                  lens === k
                    ? 'border-[var(--ink)] bg-[var(--paper)] text-[var(--ink)]'
                    : 'border-[var(--line)] text-[var(--ink-2)]'
                }`}
              >
                {LENS_WORDS[k]}
              </button>
            ))}
          </div>
          <div className="mr-3 text-right">
            <p className="mono text-[22px] leading-[24px] tabular">{elapsed}</p>
            <p className="text-[11px] leading-[14px] tracking-[0.06em] text-[var(--ink-2)] uppercase">
              since alert
            </p>
          </div>
          {live ? (
            <>
              <Button variant="primary" onClick={() => standDown.mutate()}>
                Send all-clear
              </Button>
              {/*
                The one-tap R3 safety, only while the deadline is in the future
                (migration 0064). Alongside — never instead of — the ordinary
                "Sent in error", so an admin who taps as the window closes does
                not find themselves without a control. `role="status"` on the
                countdown, not `alert` — a passive live value, not news.
              */}
              {retractSecondsLeft !== null ? (
                <Button
                  variant="primary"
                  danger
                  disabled={retract.isPending}
                  onClick={() => retract.mutate()}
                  label={`Retract this alert — ${retractSecondsLeft}s left in the one-tap window`}
                >
                  {retract.isPending ? (
                    'Retracting…'
                  ) : (
                    <>
                      Retract{' '}
                      <span role="status" aria-live="polite" className="tabular">
                        · {retractSecondsLeft}s
                      </span>
                    </>
                  )}
                </Button>
              ) : null}
              <Button onClick={() => setChanging(true)}>Change procedure</Button>
              <Button onClick={() => correct.mutate()}>Sent in error</Button>
            </>
          ) : e.state === 'all_clear_sent' || e.state === 'corrected' ? (
            <Button variant="primary" onClick={() => close.mutate()}>
              Close event
            </Button>
          ) : (
            <Status tone="safe">Closed</Status>
          )}
          <Button variant="quiet" onClick={onBack} label="Leave the live view">
            Back
          </Button>
        </div>
      </header>

      <div className="grid min-h-0 flex-1 grid-cols-1 xl:grid-cols-[360px_minmax(0,1fr)_460px]">
        {/*
          Left: are they out. The ring and its numbers, then what to do about
          it. The situation sentence comes first because "what is happening" is
          the question somebody opening this mid-incident is asking.
        */}
        <aside
          aria-label="Who was told, and what they said"
          className="flex min-h-0 flex-col overflow-y-auto border-r border-[var(--line)]"
        >
          <div className="flex flex-col gap-5 p-5">
            {/* The ring says this to the eye; the sentence says it to a screen reader. */}
            <p className="sr-only">{situation(a)}</p>
            <div className="elev-2 rounded-[var(--radius-3)] border border-[var(--line)] bg-[var(--paper-3)] p-5">
              <div className="flex items-center justify-center">
                <Ring safe={a.safe} total={a.notified} />
              </div>
              {/* The ring already says how many have not answered; only what
                  it cannot say sits beside it. Areas clear is the right
                  column's number, not this one's. */}
              {a.needsHelp > 0 || a.arrived > 0 || a.notOnSite > 0 ? (
                <div className="mt-5 grid grid-cols-2 gap-4">
                  {a.needsHelp > 0 ? (
                    <Metric label="Need help" value={a.needsHelp} tone="alert" />
                  ) : null}
                  {a.arrived > 0 ? (
                    <Metric label="At assembly point" value={a.arrived} tone="safe" />
                  ) : null}
                  {a.notOnSite > 0 ? (
                    <Metric label="Not on site" value={a.notOnSite} tone="neutral" />
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
          <Pane
            title="People"
            count={
              people.length > 0
                ? `${people.length} told · ${responded} responded`
                : `${a.notified} told · ${a.safe + a.needsHelp + a.notOnSite} responded`
            }
          >
            {people.length > 0 ? (
              <>
                {/* Filter by status: only the statuses present, each with its count. */}
                <div
                  className="mb-3 flex flex-wrap gap-1.5"
                  role="group"
                  aria-label="Show people who are"
                >
                  {(['all', ...STATUS_ORDER] as const)
                    .filter((k) => k === 'all' || (statusCounts[k] ?? 0) > 0)
                    .map((k) => {
                      const on = peopleFilter === k;
                      const n = k === 'all' ? people.length : (statusCounts[k] ?? 0);
                      return (
                        <button
                          key={k}
                          type="button"
                          aria-pressed={on}
                          onClick={() => setPeopleFilter(k)}
                          className={`rounded-[var(--radius-1)] border px-2 py-1 text-[12px] ${
                            on
                              ? 'border-[var(--ink)] bg-[var(--paper)] text-[var(--ink)]'
                              : 'border-[var(--line)] text-[var(--ink-2)]'
                          }`}
                        >
                          {k === 'all' ? 'All' : STATUS_WORDS[k].chip}{' '}
                          <span className="tabular">{n}</span>
                        </button>
                      );
                    })}
                </div>
                <ul className="m-0 list-none divide-y divide-[var(--line)] p-0">
                  {shown.map((p) => (
                    <li key={p.user_id} className="py-2 text-[14px]">
                      <div className="flex items-center gap-3">
                        <span className="min-w-0 flex-1 truncate">{p.label}</span>
                        <Status tone={STATUS_WORDS[p.status].tone}>
                          {STATUS_WORDS[p.status].row}
                          {p.status === 'safe_arrived' || p.status === 'arrived'
                            ? ` · ${p.arrived_at ?? 'assembly point'}`
                            : ''}
                          {p.status === 'needs_help' && p.answered_at
                            ? ` · ${Math.max(0, Math.round((now - new Date(p.answered_at).getTime()) / 60000))}m`
                            : ''}
                        </Status>
                      </div>
                      {p.note ? (
                        <p className="mt-0.5 text-[13px] text-[var(--ink-2)]">“{p.note}”</p>
                      ) : null}
                      {assistanceByUser[p.user_id] ? (
                        <p className="mt-0.5 text-[13px] text-[var(--ink-2)]">
                          Needs assistance to get out: {assistanceByUser[p.user_id]!.plan} ·{' '}
                          {assistanceByUser[p.user_id]!.assistant
                            ? `with ${assistanceByUser[p.user_id]!.assistant}`
                            : 'nobody assigned'}
                          {assistanceByUser[p.user_id]!.goesTo
                            ? ` · to ${assistanceByUser[p.user_id]!.goesTo}`
                            : ''}
                        </p>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </>
            ) : a.notified > 0 ? (
              <p className="text-[13px] text-[var(--ink-2)]">
                {a.notified} {a.notified === 1 ? 'person was' : 'people were'} told. Their names and
                answers need a newer server.
              </p>
            ) : (
              <p className="text-[13px] text-[var(--ink-2)]">Nobody has been told yet.</p>
            )}
          </Pane>
        </aside>

        {/*
          Centre: the map, the full height of the viewport — every area
          coloured by sweep state, the points, the building. The key floats
          over it as a glass chip rather than taking a row beneath it.
        */}
        <section className="relative min-h-[320px]" aria-label="Site and assembly points">
          <Map points={points} height="100%" dark />
          <div className="sheet elev-1 pointer-events-none absolute bottom-3 left-3 z-10 rounded-[var(--radius-2)] border border-[var(--glass-edge)] px-3 py-2">
            <MapKey kinds={[...new Set(points.map((p) => p.kind))]} />
            {unplacedZones > 0 ? (
              <p className="mt-1 text-[12px] text-[var(--ink-2)]">
                {unplacedZones} {unplacedZones === 1 ? 'area has' : 'areas have'} no location set,
                so {unplacedZones === 1 ? 'it is' : 'they are'} in Areas but not on the map.
              </p>
            ) : null}
          </div>
          {/* The stream tile (evac-evidence 03.05). Renders nothing while STREAM_PROVIDER=none. */}
          <div className="absolute top-3 right-3 z-10 w-[320px]">
            <StreamTile orgId={orgId} eventId={eventId} />
          </div>
        </section>

        {/*
          Right: three panes that scroll inside themselves — Areas, People,
          Delivery — then Command. Each opens on its headline count; the page
          never scrolls.
        */}
        <aside
          aria-label="Managing the response"
          className="flex min-h-0 flex-col overflow-y-auto border-l border-[var(--line)]"
        >
          {/*
            The panes, in the order this lens wants them: command opens on
            Do next; a warden on their own areas; security on who is in
            command and the ground. Every pane is always present — the lens
            only decides what comes first.
          */}
          {orderPanes(
            [
              {
                key: 'next',
                node: (
                  <Pane
                    title="Do next"
                    count={doNext.length === 0 ? undefined : String(doNext.length)}
                  >
                    {doNext.length === 0 ? (
                      <p className="text-[14px] text-[var(--ink-2)]">
                        Nothing needs you right now.
                      </p>
                    ) : (
                      <ol className="m-0 flex list-none flex-col gap-2 p-0">
                        {doNext.map((d) => (
                          <li key={d.key}>
                            <Finding tone={d.tone}>{d.text}</Finding>
                          </li>
                        ))}
                      </ol>
                    )}
                  </Pane>
                ),
              },
              {
                key: 'areas',
                node: (
                  <Pane
                    title="Areas"
                    count={
                      sweeps.data ? `${sweeps.data.swept} / ${sweeps.data.total} clear` : undefined
                    }
                  >
                    {/*
                    ONE table for areas: the warden board (who sweeps what, notified /
                    on it / done / state, reassign) carrying the sweep's headcount and
                    the Report control — never the same areas listed twice (owner,
                    2026-10-13). Somebody the server gives no command board sees the
                    plain sweep board instead.
                  */}
                    <WardenAreas
                      orgId={orgId}
                      eventId={eventId}
                      headcountOf={(zoneId) =>
                        sweeps.data?.zones.find((z) => z.zoneId === zoneId)?.headcount ?? null
                      }
                      report={(z) => (
                        <SweepControl
                          orgId={orgId}
                          eventId={eventId}
                          zoneId={z.zoneId}
                          zoneName={z.zoneName}
                        />
                      )}
                      fallback={
                        disclosure.isVisible('sweepBoard') ? (
                          sweeps.data && sweeps.data.zones.length > 0 ? (
                            <Table head={['Area', 'Warden', 'State', 'Count', 'Heard', '']}>
                              {sweeps.data.zones.map((z) => (
                                <Row key={z.zoneId}>
                                  <Cell wide>{z.zoneName}</Cell>
                                  <Cell>
                                    {z.warden ?? <Status tone="attention">Nobody assigned</Status>}
                                  </Cell>
                                  <Cell>
                                    <Status
                                      tone={
                                        z.state === 'clear'
                                          ? 'safe'
                                          : z.state === 'not_started'
                                            ? 'neutral'
                                            : 'attention'
                                      }
                                    >
                                      {sentence(z.state.replace(/_/g, ' '))}
                                    </Status>
                                  </Cell>
                                  <Cell numeric>{z.headcount ?? '—'}</Cell>
                                  <Cell numeric>
                                    {z.secondsSinceReport === null
                                      ? '—'
                                      : `${Math.round(z.secondsSinceReport / 60)}m`}
                                  </Cell>
                                  <Cell>
                                    <SweepControl
                                      orgId={orgId}
                                      eventId={eventId}
                                      zoneId={z.zoneId}
                                      zoneName={z.zoneName}
                                    />
                                  </Cell>
                                </Row>
                              ))}
                            </Table>
                          ) : (
                            <Empty>
                              No areas are defined for this site, so no sweep is possible.
                            </Empty>
                          )
                        ) : null
                      }
                    />
                  </Pane>
                ),
              },
              {
                key: 'ground',
                node: (
                  <Pane title="Points and hazards" count={groundCount}>
                    <GroundPane
                      orgId={orgId}
                      eventId={eventId}
                      active={live}
                      kind={pointKind}
                      direct={direct}
                    />
                  </Pane>
                ),
              },
              {
                key: 'reports',
                node: (
                  <Pane
                    title="Reports from the ground"
                    count={paneCount(newReportCount(direct.reports.data?.reports), 'to decide')}
                  >
                    <ReportsPane orgId={orgId} eventId={eventId} direct={direct} />
                  </Pane>
                ),
              },
              {
                key: 'command',
                node: (
                  <Pane title="Who is in command">
                    {command.data ? (
                      <Table head={['Role', 'Holder', 'How']}>
                        {command.data.roles.map((r) => (
                          <Row key={r.role}>
                            <Cell wide>{humanRole(r.role)}</Cell>
                            <Cell>{r.holder ?? <Status tone="attention">Vacant</Status>}</Cell>
                            <Cell>{r.source}</Cell>
                          </Row>
                        ))}
                      </Table>
                    ) : (
                      <Spinner />
                    )}
                    <Handover orgId={orgId} eventId={eventId} />
                    <Escalations orgId={orgId} only="open" />
                    {siteId ? (
                      <EscalateBox orgId={orgId} eventId={eventId} siteId={siteId} />
                    ) : null}
                    {event.data.orgShare ? <OrgThanks share={event.data.orgShare} /> : null}
                  </Pane>
                ),
              },
            ],
            lens,
          ).map((pane) => (
            <Fragment key={pane.key}>{pane.node}</Fragment>
          ))}
        </aside>
      </div>

      {changing ? (
        <TransitionDialog
          currentProcedure={e.procedure}
          stored={storedProcedures.data?.procedures}
          pending={changeProcedure.isPending}
          error={changeProcedure.error}
          onSubmit={(body) => changeProcedure.mutate(body)}
          onDismiss={() => {
            changeProcedure.reset();
            setChanging(false);
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * One pane of the live dashboard's right column: a heading with its
 * headline count, open by default, scrolling inside itself. A pane with
 * nothing demanding reads as one line.
 */
function Pane({
  title,
  count,
  open = true,
  children,
}: {
  title: string;
  count?: string;
  open?: boolean;
  children: ReactNode;
}) {
  return (
    <details open={open} className="group border-b border-[var(--line)]">
      <summary className="flex cursor-pointer items-center justify-between gap-3 px-4 py-3 text-[14px] font-[650] select-none">
        <h2 className="m-0 text-[14px] font-[650]">{title}</h2>
        {count ? (
          <span className="tabular text-[12px] font-normal text-[var(--ink-2)]">{count}</span>
        ) : null}
      </summary>
      <div className="px-4 pb-4">{children}</div>
    </details>
  );
}

/**
 * Occupier → operator escalation, from the Live view.
 *
 * The API only accepts it when a confirmed `venue_tenancies` handshake exists
 * between the event's site and the venue. So we render nothing unless there is
 * a valid target: a Ready button with an empty target list is worse than no
 * button at all, and this control appears only when the whole loop works.
 */
function EscalateBox({
  orgId,
  eventId,
  siteId,
}: {
  orgId: string;
  eventId: string;
  siteId: string;
}) {
  const qc = useQueryClient();
  const [venueId, setVenueId] = useState('');
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);
  const tenancies = useQuery({
    queryKey: ['tenancies', orgId],
    queryFn: () => api.getTenancies(orgId),
  });
  const confirmed = (tenancies.data?.tenancies ?? []).filter(
    (t) => t.state === 'confirmed' && t.mine && t.site_id === siteId,
  );

  const send = useMutation({
    mutationFn: () => api.escalateEvent(orgId, eventId, venueId, body.trim()),
    onSuccess: () => {
      setError(null);
      setOk(true);
      setBody('');
      void qc.invalidateQueries({ queryKey: ['escalations', orgId] });
    },
    onError: (e: Error) => {
      setOk(false);
      setError(e.message);
    },
  });

  if (confirmed.length === 0) return null;

  return (
    <Section title="Escalate to the centre">
      <p className="mb-3 max-w-[640px] text-[13px] text-[var(--ink-2)]">
        Tell the venue operator something is happening. Not complete until a human at the centre
        acknowledges it — this row moves to "Waiting to be acknowledged" the moment you send.
      </p>
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-[280px]">
          <Select
            label="Venue"
            value={venueId || confirmed[0]!.venue_id}
            onChange={setVenueId}
            options={confirmed.map((t) => ({ value: t.venue_id, label: t.venue_name }))}
          />
        </div>
        <div className="w-[360px]">
          <Field
            label="What is happening"
            value={body}
            onChange={setBody}
            placeholder="Fire alarm on the second floor. Evacuating now."
          />
        </div>
        <Button
          variant="primary"
          disabled={!body.trim() || send.isPending}
          onClick={() => send.mutate()}
        >
          Send
        </Button>
      </div>
      {ok ? (
        <div className="mt-3">
          <Finding tone="safe">Sent. Watch the acknowledgement above.</Finding>
        </div>
      ) : null}
      {error ? (
        <div className="mt-3">
          <Finding>{error}</Finding>
        </div>
      ) : null}
    </Section>
  );
}

/**
 * File a sweep report from the dashboard.
 *
 * Until now the board was read-only here: a warden with a phone could report,
 * and the communications officer taking reports over a radio could not write
 * them down anywhere the system could see. `staff` on the API, append-only in
 * the schema — this control adds a row, never edits one.
 */
/**
 * The organisation's own thanks, ready to post.
 *
 * Third-party telling is the one form of public recognition that raises the
 * helpers' standing rather than lowering it — a person posting about their
 * own good deed is read as credit-seeking and rated *less* generous (the
 * Braggart's Dilemma), so the org speaks and names nobody
 * (docs/gamification.md §Sharing).
 *
 * The wording is the server's, not this screen's: the app and anything
 * later must say the same sentence. Copy, don't post — we never publish on
 * anybody's behalf, and the admin gets to read it before their community
 * does.
 */
function OrgThanks({ share }: { share: { text: string; url: string } }) {
  const [copied, setCopied] = useState(false);

  return (
    <div className="mt-10 border-t border-[var(--line)] pt-8">
      <h2 className="mb-1 text-[14px] font-[650]">Thank the people who worked it</h2>
      <p className="mb-3 text-[13px] text-[var(--ink-2)]">
        Your words, your voice, nobody named. Post it wherever your community already reads you.
      </p>
      <p className="rounded-[var(--radius-1)] border border-[var(--line)] p-4 text-[14px] leading-[1.6]">
        {share.text}
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Button
          onClick={() => {
            void navigator.clipboard.writeText(`${share.text} ${share.url}`).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            });
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
        <a
          href={share.url}
          target="_blank"
          rel="noreferrer"
          className="text-[13px] underline underline-offset-2"
        >
          See what people will open
        </a>
      </div>
    </div>
  );
}

function SweepControl({
  orgId,
  eventId,
  zoneId,
  zoneName,
}: {
  orgId: string;
  eventId: string;
  zoneId: string;
  zoneName: string;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [state, setState] = useState('clear');
  const [headcount, setHeadcount] = useState('');
  const [note, setNote] = useState('');

  const file = useMutation({
    mutationFn: () =>
      api.fileSweep(orgId, eventId, {
        zoneId,
        state,
        ...(headcount.trim() !== '' ? { headcount: Number(headcount) } : {}),
        ...(note.trim() !== '' ? { note: note.trim() } : {}),
      }),
    onSuccess: () => {
      setOpen(false);
      setHeadcount('');
      setNote('');
      void qc.invalidateQueries({ queryKey: ['sweeps', orgId, eventId] });
    },
  });

  if (!open) {
    return (
      <Button variant="quiet" label={`Report on ${zoneName}`} onClick={() => setOpen(true)}>
        Report
      </Button>
    );
  }

  const fieldClass =
    'h-8 rounded-[var(--radius-1)] border border-[var(--line-strong)] bg-[var(--paper)] px-2 text-[13px] text-[var(--ink)]';

  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <select
        className={fieldClass}
        value={state}
        onChange={(ev) => setState(ev.target.value)}
        aria-label={`State of ${zoneName}`}
      >
        <option value="sweeping">Sweeping</option>
        <option value="clear">Clear</option>
        <option value="cannot_access">Cannot access</option>
        <option value="needs_assistance">Needs assistance</option>
      </select>
      <input
        className={`${fieldClass} w-[72px]`}
        inputMode="numeric"
        placeholder="Count"
        value={headcount}
        onChange={(ev) => setHeadcount(ev.target.value.replace(/[^0-9]/g, ''))}
        aria-label={`Headcount for ${zoneName}`}
      />
      <input
        className={`${fieldClass} w-[160px]`}
        placeholder="Note"
        value={note}
        onChange={(ev) => setNote(ev.target.value)}
        aria-label={`Note for ${zoneName}`}
      />
      <Button
        variant="primary"
        disabled={file.isPending}
        label={`File report for ${zoneName}`}
        onClick={() => file.mutate()}
      >
        File
      </Button>
      <Button
        variant="quiet"
        label={`Cancel report for ${zoneName}`}
        onClick={() => setOpen(false)}
      >
        Cancel
      </Button>
    </span>
  );
}

/**
 * Command passes to a named person, now, on the record.
 *
 * Migration 0023: append-only, may pass outside the written succession line
 * (the deputy is off site and a floor warden steps up — recorded, not
 * refused), `staff` with no freshness gate because the person taking over is
 * often the least senior account in the building.
 */
function Handover({ orgId, eventId }: { orgId: string; eventId: string }) {
  const qc = useQueryClient();
  const roster = useQuery({ queryKey: ['roster', orgId], queryFn: () => api.getRoster(orgId) });
  const [open, setOpen] = useState(false);
  const [userId, setUserId] = useState('');
  const [role, setRole] = useState('chief_warden');
  const [reason, setReason] = useState('');

  const hand = useMutation({
    mutationFn: () =>
      api.recordHandover(orgId, eventId, { userId, role, reason: reason || undefined }),
    onSuccess: () => {
      setOpen(false);
      setReason('');
      void qc.invalidateQueries({ queryKey: ['command', orgId, eventId] });
    },
  });

  if (!open) {
    return (
      <div className="mt-4">
        <Button variant="quiet" label="Record a change of command" onClick={() => setOpen(true)}>
          Record a change of command
        </Button>
      </div>
    );
  }

  const people = (roster.data?.members ?? []).filter((m) => m.deprovisioned_at === null);
  const fieldClass =
    'h-9 rounded-[var(--radius-1)] border border-[var(--line-strong)] bg-[var(--paper)] px-2 text-[14px] text-[var(--ink)]';

  return (
    <div className="mt-4 flex flex-wrap items-center gap-2">
      <select
        className={fieldClass}
        value={role}
        onChange={(ev) => setRole(ev.target.value)}
        aria-label="Role changing hands"
      >
        {[
          'chief_warden',
          'deputy_chief_warden',
          'communications_officer',
          'area_warden',
          'floor_warden',
          'warden',
        ].map((r) => (
          <option key={r} value={r}>
            {humanRole(r)}
          </option>
        ))}
      </select>
      <select
        className={fieldClass}
        value={userId}
        onChange={(ev) => setUserId(ev.target.value)}
        aria-label="Who takes command"
      >
        <option value="">Who takes it…</option>
        {people.map((m) => (
          <option key={m.user_id} value={m.user_id}>
            {m.display_name ?? m.email}
          </option>
        ))}
      </select>
      <input
        className={`${fieldClass} w-[220px]`}
        placeholder="Why (optional)"
        value={reason}
        onChange={(ev) => setReason(ev.target.value)}
        aria-label="Reason for the handover"
      />
      <Button
        variant="primary"
        disabled={!userId || hand.isPending}
        label="Record the handover"
        onClick={() => hand.mutate()}
      >
        Record
      </Button>
      <Button variant="quiet" label="Cancel the handover" onClick={() => setOpen(false)}>
        Cancel
      </Button>
    </div>
  );
}

/* ------------------------------------------------------------------ events */

/**
 * The venue boundary, both directions. An inbound escalation is a tenant
 * telling this organisation something is happening in its building, and it
 * repeats until somebody presses this button — the ack is the receipt that
 * answers "Kmart told Westfield; what did Westfield do?"
 */
export function Escalations({
  orgId,
  only,
}: {
  orgId: string;
  /**
   * `open` is the live half — raised and not yet acknowledged, so somebody has
   * to act. `settled` is the record.
   *
   * They are the same rows and two different jobs, which is why they render on
   * two different screens (`design-dashboard.md` §8a). Events is where history
   * is reviewed and reports are generated; a thing that is paging somebody
   * right now does not belong in a list you filter.
   */
  only: 'open' | 'settled';
}) {
  const qc = useQueryClient();
  const escalations = useQuery({
    queryKey: ['escalations', orgId],
    queryFn: () => api.getEscalations(orgId),
    refetchInterval: 30_000,
  });

  const ack = useMutation({
    mutationFn: (id: string) => api.ackEscalation(orgId, id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['escalations', orgId] }),
  });

  const rows = (escalations.data?.escalations ?? []).filter((e) =>
    only === 'open' ? !e.acknowledged_at : Boolean(e.acknowledged_at),
  );
  if (rows.length === 0) return null;

  return (
    <Section title={only === 'open' ? 'Waiting to be acknowledged' : 'Escalations'}>
      <Table head={['When', 'From / To', 'What they said', 'Status', '']}>
        {rows.map((e) => (
          <Row key={e.id}>
            <Cell>{new Date(e.created_at).toLocaleString()}</Cell>
            <Cell>
              {e.direction === 'inbound'
                ? `${e.occupier_name ?? 'A tenant'} (${e.site_name ?? 'site'})`
                : `To ${e.venue_name}`}
            </Cell>
            <Cell wide>{e.body}</Cell>
            <Cell>
              {e.acknowledged_at ? (
                <Status tone="safe">
                  Acknowledged{e.acknowledged_by ? ` by ${e.acknowledged_by}` : ''}
                </Status>
              ) : (
                <Status tone="alert">Unacknowledged — paging repeats</Status>
              )}
            </Cell>
            <Cell>
              {e.direction === 'inbound' && !e.acknowledged_at ? (
                <Button
                  variant="primary"
                  disabled={ack.isPending}
                  label={`Acknowledge the escalation from ${e.occupier_name ?? 'the tenant'}`}
                  onClick={() => ack.mutate(e.id)}
                >
                  Acknowledge
                </Button>
              ) : null}
            </Cell>
          </Row>
        ))}
      </Table>
    </Section>
  );
}

export function Events({
  orgId,
  role,
  onOpen,
  onOpenReport,
  siteName = null,
  embedded = false,
}: {
  orgId: string;
  /**
   * The current user's role in this org. Owner and Admin see the close
   * controls; anyone else does not. The server refuses either way, but a
   * button that always 404s is worse than no button.
   */
  role: api.Role;
  onOpen: (eventId: string) => void;
  onOpenReport: (eventId: string) => void;
  /**
   * The per-screen Site filter, from the Activity header band (owner decision
   * 2026-09-08 — the global rail site switcher is gone). When set, the feed
   * shows only the events at the building with this name; `null` shows every
   * site. Matched on the site name the list row already carries, so it is a
   * UI-only narrowing over the data already fetched — no API change.
   */
  siteName?: string | null;
  /**
   * When composed under the Activity rail screen, the shared rail-directory
   * shell (`directory.tsx`) carries the "Activity" title in its sticky header,
   * so this suppresses the feed's own PageTitle to avoid a second one. Default
   * off, so the standalone Events screen (and its a11y test) is untouched.
   */
  embedded?: boolean;
}) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  /**
   * Drill or real — the one distinction that must never blur (R2). It is the
   * loudest thing in every row already; a scoped view-switch lets an admin
   * reviewing the record read one kind at a time without it being navigation
   * (the rail) or a new query (this filters what is already fetched).
   */
  const [kind, setKind] = useState<'all' | 'drill' | 'live'>('all');
  const events = useQuery({ queryKey: ['events', orgId], queryFn: () => api.listEvents(orgId) });
  const me = useQuery({ queryKey: ['me'], queryFn: api.getMe });
  const canClose = api.isAdminRole(role);
  /**
   * A close/stand-down that hit `stale_session`, held here until the
   * reverify modal returns success (see docs/security.md R1 freshness gate
   * and web/src/reverify.tsx). The retry runs whatever action was refused —
   * the operator never has to sign out mid-incident.
   */
  const [reverify, setReverify] = useState<null | (() => void)>(null);

  /**
   * R1's second pair of eyes, from the list.
   *
   * The database refuses the same person, so a 4xx here is an operator-facing
   * sentence rather than the enforcement. Whatever it says, it is the server's
   * answer and not this screen's guess.
   */
  const authorise = useMutation({
    mutationFn: (eventId: string) => api.authoriseEvent(orgId, eventId),
    onSuccess: (res, eventId) => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ['events', orgId] });
      if (res?.state === 'active') onOpen(eventId);
    },
    onError: (e: Error) => setError(e.message),
  });

  /**
   * Close an event from the row.
   *
   * Two ends of the same job. An event in `all_clear_sent` or `corrected`
   * needs one call to `/close`; an event that is still `active` has to be
   * stood down first (Hawaii's lesson — the database refuses `active →
   * closed`, so this UI cannot skip the step either). The mutation
   * variable carries the state so the second half of the chain is
   * conditional rather than always fired.
   *
   * Freshness: `/all-clear` and `/close` both sit behind `requireFresh`.
   * A stale session comes back as 403 with `code: 'stale_session'`; we do
   * not have an inline re-verify path yet, so the copy names the fix.
   */
  const closeMut = useMutation({
    mutationFn: async ({ eventId, state }: { eventId: string; state: string }) => {
      if (state === 'active') await api.allClear(orgId, eventId);
      await api.closeEvent(orgId, eventId);
    },
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ['events', orgId] });
    },
    onError: (e: Error, variables) => {
      if (e instanceof api.ApiError && e.detail.code === 'stale_session') {
        // Open the reverify modal; on success we retry the exact call the
        // operator asked for. No "sign out and back in" during an incident.
        setReverify(() => () => closeMut.mutate(variables));
        return;
      }
      setError(e.message);
    },
  });

  /**
   * Generate the signed report from the row.
   *
   * Before this, a closed event that had no report yet showed an em-dash in
   * the Report column, and the "Generate report" button was hidden inside
   * the Reports section beneath — which only rendered *if* a row had been
   * opened. An operator finishing a drill and hitting close had to open the
   * event, scroll, click a button they had never been told about. Inline
   * generation makes the row's Report cell the one place that speaks.
   */
  const genMut = useMutation({
    mutationFn: (eventId: string) => api.generateReport(orgId, eventId),
    onSuccess: (_, eventId) => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ['events', orgId] });
      onOpenReport(eventId);
    },
    onError: (e: Error) => setError(e.message),
  });

  if (events.isLoading || !events.data) {
    return (
      <>
        {embedded ? null : (
          <PageTitle sub="Every drill and incident, the report it produced, and who did what.">
            Activity
          </PageTitle>
        )}
        <Spinner />
      </>
    );
  }

  return (
    <>
      {embedded ? null : (
        <PageTitle sub="Every drill and incident, the report it produced, and who did what.">
          Activity
        </PageTitle>
      )}

      {error ? (
        <div className="mb-6 max-w-[640px]">
          <Finding>{error}</Finding>
        </div>
      ) : null}

      {reverify && me.data?.email ? (
        <ReverifyModal
          email={me.data.email}
          onClose={() => setReverify(null)}
          onVerified={() => {
            const retry = reverify;
            setReverify(null);
            retry();
          }}
        />
      ) : null}

      <Escalations orgId={orgId} only="settled" />

      {events.data.events.length === 0 ? (
        <Empty>Nothing has been run yet. Start a drill from Raise an alert.</Empty>
      ) : (
        (() => {
          const shown = events.data.events.filter(
            (e) => (kind === 'all' || e.mode === kind) && (!siteName || e.site_name === siteName),
          );
          return (
            <>
              <div className="mb-4">
                <Segmented
                  label="Filter by kind"
                  value={kind}
                  onChange={setKind}
                  options={[
                    { value: 'all', label: 'All' },
                    { value: 'drill', label: 'Drills' },
                    { value: 'live', label: 'Real' },
                  ]}
                />
              </div>
              {shown.length === 0 ? (
                <Empty>
                  {siteName
                    ? `No ${kind === 'live' ? 'real emergencies' : kind === 'drill' ? 'drills' : 'events'} at ${siteName}. Choose “All sites” to see every building.`
                    : kind === 'live'
                      ? 'No real emergencies have been raised — every event here has been a drill.'
                      : 'No drills have been run yet.'}
                </Empty>
              ) : (
                <Table head={['When', 'Site', 'What', 'State', 'Answered', 'Report', '']}>
                  {shown.map((e) => (
                    <Row key={e.id}>
                      <Cell>{new Date(e.activated_at ?? e.created_at).toLocaleString()}</Cell>
                      <Cell wide>{e.site_name}</Cell>
                      <Cell>
                        {/* Drill vs live is the one distinction that must never blur
                    (R2), so it is stated in the row, not inferred from colour.
                    The leading glyph names the procedure type; the words carry
                    both facts. */}
                        <span className="inline-flex items-center gap-2">
                          <ProcedureGlyph procedure={e.procedure} />
                          <span>
                            <Status tone={e.mode === 'live' ? 'alert' : 'neutral'}>
                              {e.mode === 'live' ? 'Live' : 'Drill'}
                            </Status>{' '}
                            {sentence(e.procedure)}
                          </span>
                        </span>
                      </Cell>
                      <Cell>
                        {/*
                  An event waiting for a second admin is not a state, it is a
                  job — and it lapses. So the row that names it carries the way
                  to do it, rather than making somebody open the event to find
                  out they are the person it is waiting for (R1, §7c).
                */}
                        {e.state === 'pending_second_confirm' ? (
                          <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
                            <Status tone="alert">Waiting for you</Status>
                            <InlineAction onClick={() => authorise.mutate(e.id)}>
                              Authorise
                            </InlineAction>
                          </span>
                        ) : (
                          sentence(e.state.replace(/_/g, ' '))
                        )}
                      </Cell>
                      <Cell numeric>{e.notified > 0 ? `${e.safe} / ${e.notified}` : '—'}</Cell>
                      <Cell>
                        {e.has_report ? (
                          <Button
                            variant="quiet"
                            label={`Open the signed report for ${e.site_name}`}
                            onClick={() => onOpenReport(e.id)}
                          >
                            <Status tone="safe">Signed</Status>
                          </Button>
                        ) : e.state === 'closed' || e.state === 'corrected' ? (
                          // Inline generation: the row that finished the event is the
                          // row that offers its report. Server-side the content is a
                          // pure function of the closed event, so this button is safe
                          // to press once — the same bytes come back on any regenerate.
                          <Button
                            variant="quiet"
                            label={`Generate signed report for ${e.site_name}`}
                            disabled={genMut.isPending}
                            onClick={() => genMut.mutate(e.id)}
                          >
                            {genMut.isPending && genMut.variables === e.id
                              ? 'Generating…'
                              : 'Generate'}
                          </Button>
                        ) : (
                          '—'
                        )}
                      </Cell>
                      <Cell>
                        <span className="inline-flex flex-wrap items-center gap-2">
                          <Button
                            variant="quiet"
                            label={`Open ${e.site_name} ${e.mode}`}
                            onClick={() => onOpen(e.id)}
                          >
                            Open
                          </Button>
                          {/*
                    Close from the row.

                    A Parramatta drill sat in `all_clear_sent` for two days
                    (2026-08-30), because there was no way to close an event
                    from anywhere except the Live view — and it kept
                    surfacing on every cold-open of the mobile app as
                    "Drill over" until Trav hand-rolled a curl to POST
                    /close.

                    `active` needs the two-step chain because the database
                    refuses a direct `active → closed`; the button label
                    names both actions so the operator knows what they are
                    approving in the confirm.
                  */}
                          {canClose && (e.state === 'all_clear_sent' || e.state === 'corrected') ? (
                            <Button
                              variant="quiet"
                              disabled={closeMut.isPending}
                              label={`Close ${e.site_name} ${e.mode}`}
                              onClick={() => {
                                if (
                                  window.confirm(
                                    `Close this ${e.mode} at ${e.site_name}? A closed event is the record — it cannot be reopened.`,
                                  )
                                ) {
                                  closeMut.mutate({ eventId: e.id, state: e.state });
                                }
                              }}
                            >
                              Close
                            </Button>
                          ) : null}
                          {canClose && e.state === 'active' ? (
                            <Button
                              variant="quiet"
                              disabled={closeMut.isPending}
                              label={`Stand down and close ${e.site_name} ${e.mode}`}
                              onClick={() => {
                                if (
                                  window.confirm(
                                    `Stand down and close this ${e.mode} at ${e.site_name}? Everyone alerted is told it is over, then the event is closed as the record.`,
                                  )
                                ) {
                                  closeMut.mutate({ eventId: e.id, state: e.state });
                                }
                              }}
                            >
                              Stand down + close
                            </Button>
                          ) : null}
                        </span>
                      </Cell>
                    </Row>
                  ))}
                </Table>
              )}
            </>
          );
        })()
      )}
    </>
  );
}

/* ----------------------------------------------------------------- reports */

/**
 * One question: **can this be put in front of a regulator?**
 *
 * The hash and the signature are shown as the document's own footer shows them,
 * because the point of printing a hash is that somebody compares it. An
 * unsigned report says so on its face rather than looking signed and failing
 * verification later.
 */
/**
 * Run the same check a regulator would.
 *
 * The verify endpoint is unauthenticated on purpose — this button proves that
 * to the admin by using it exactly as an outsider would: post the document
 * and its signature, get a verdict. Nothing here trusts the session.
 */
function Verify({ report }: { report: api.StoredReport }) {
  const check = useMutation({
    mutationFn: () =>
      api.verifyReport({
        content: report.content,
        signature: report.signature,
        signingKeyId: report.signingKeyId,
        expectedContentHash: report.contentHash,
      }),
  });

  return (
    <Section title="Check it">
      <p className="mb-4 max-w-prose text-[14px] text-[var(--ink-2)]">
        Anyone holding this document can verify it against the public key, with no account. This
        runs that exact check.
      </p>
      {check.data ? (
        <Finding>
          <Status
            tone={
              check.data.signatureValid === true && check.data.hashMatches !== false
                ? 'safe'
                : check.data.signatureValid === false || check.data.hashMatches === false
                  ? 'alert'
                  : 'neutral'
            }
          >
            {check.data.verdict}
          </Status>
        </Finding>
      ) : (
        <Button
          variant="secondary"
          disabled={check.isPending}
          label="Verify this report independently"
          onClick={() => check.mutate()}
        >
          Verify independently
        </Button>
      )}
    </Section>
  );
}

/**
 * "Open printable version" that actually opens.
 *
 * The report route is bearer-authenticated, but a plain `<a target="_blank">`
 * never sends headers on the new-tab request and every operator who tried it
 * on staging got a 401. Fetch the HTML with credentials, hand the browser a
 * Blob URL, and Print → Save as PDF still runs off the same self-contained
 * document — which is the whole design (`api/src/routes/reports.ts:305-321`).
 * The blob URL is revoked when this component unmounts or fetches again, so
 * we do not leak object URLs across multiple opens.
 */
function OpenPrintable({ orgId, eventId }: { orgId: string; eventId: string }) {
  const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('idle');

  const open = async () => {
    setStatus('loading');
    try {
      const html = await api.fetchReportHtml(orgId, eventId);
      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      // Open in a new tab. The browser gates window.open outside a user
      // gesture; this callback runs on click, so it is allowed. Popup blockers
      // still catch it in some contexts, in which case status stays 'idle'
      // and the operator sees the button ready to try again.
      const tab = window.open(url, '_blank', 'noopener,noreferrer');
      // Revoke shortly after — the tab has cloned the bytes; the URL only
      // needs to survive the initial navigation.
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
      setStatus(tab ? 'idle' : 'error');
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="flex items-center gap-3">
      <Button
        variant="quiet"
        label="Open the printable version of this report"
        disabled={status === 'loading'}
        onClick={() => void open()}
      >
        {status === 'loading' ? 'Opening…' : 'Open printable version'}
      </Button>
      {status === 'error' ? (
        <span className="text-[13px] text-[var(--attention)]">
          Could not open. Check pop-ups are allowed and try again.
        </span>
      ) : null}
    </div>
  );
}

export function Reports({
  orgId,
  eventId,
  evidenceEnabled,
  onOpenEvidence,
}: {
  orgId: string;
  eventId: string | null;
  /**
   * Whether this org has the Evidence module. Only then is the cross-link to the
   * court-grade evidence workspace shown — a non-module org must never see a link
   * into a surface it cannot open (the two families are named, but only bridged
   * where the bridge leads somewhere).
   */
  evidenceEnabled?: boolean;
  onOpenEvidence?: () => void;
}) {
  const qc = useQueryClient();

  const report = useQuery({
    queryKey: ['report', orgId, eventId],
    queryFn: () => api.getReport(orgId, eventId!),
    enabled: Boolean(eventId),
    retry: false,
  });

  const generate = useMutation({
    mutationFn: () => api.generateReport(orgId, eventId!),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['report', orgId, eventId] }),
  });

  if (!eventId) {
    return (
      <>
        <PageTitle sub="The signed, tamper-evident account of every closed event — verifiable offline.">
          Signed records
        </PageTitle>
        <Empty>Open a closed event to generate or read its signed record.</Empty>
      </>
    );
  }

  return (
    <>
      <PageTitle sub="The signed, tamper-evident account of every closed event — verifiable offline.">
        Signed records
      </PageTitle>

      {/*
        The bridge between the two evidence families, drawn only for an org that
        has both: these signed event records, and the separate court-grade
        evidence workspace for incident recordings and their chain of custody.
      */}
      {evidenceEnabled && onOpenEvidence ? (
        <div className="mb-6">
          <InlineAction onClick={onOpenEvidence}>
            Duress Evidence — court-grade incident recordings →
          </InlineAction>
        </div>
      ) : null}

      {report.isLoading ? (
        <Spinner />
      ) : report.data ? (
        <>
          <Section title="Document" action={<OpenPrintable orgId={orgId} eventId={eventId} />}>
            <Metrics>
              <Metric
                label="Generated"
                value={new Date(report.data.generatedAt).toLocaleDateString()}
                sub={report.data.generatedBy ?? undefined}
              />
              <Metric
                label="Signature"
                value={report.data.signature ? 'Signed' : 'Unsigned'}
                tone={report.data.signature ? 'safe' : 'attention'}
              />
            </Metrics>

            <div className="mt-6">
              <div className="mb-1">
                <Label>Content hash</Label>
              </div>
              <Mono>{report.data.contentHash}</Mono>
            </div>
          </Section>

          <Verify report={report.data} />
        </>
      ) : (
        <Empty
          action={
            <Button
              variant="primary"
              onClick={() => generate.mutate()}
              disabled={generate.isPending}
            >
              Generate report
            </Button>
          }
        >
          No report has been generated for this event yet.
        </Empty>
      )}
    </>
  );
}
